import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { HelpCircle, ChevronDown, Gift, Package, Truck, MapPin, CreditCard, Clock, Image, RefreshCw, X, ShoppingCart, Shield, Phone, MessageCircle, Mail, Globe } from 'lucide-react';
import { Button } from '../components/ui/Button';
const WHATSAPP_NUMBER = '27732736906';
interface FAQItem {
  icon: React.ElementType;
  question: string;
  answer: string | string[];
}
const faqs: FAQItem[] = [{
  icon: Gift,
  question: '1. What products do you sell?',
  answer: ['🎈 Balloons', '💐 Flowers', '🎁 Gift hampers / gift boxes', '✨ Custom gift arrangements for special occasions']
}, {
  icon: Package,
  question: '2. Do you offer custom orders?',
  answer: "Yes! We do custom orders based on your budget, occasion, and preferences. Just tell us what you want and we'll design something special for you."
}, {
  icon: Truck,
  question: '3. Do you offer delivery?',
  answer: 'Yes, we offer delivery. Delivery fees depend on your location. We will confirm the delivery cost before finalizing your order.'
}, {
  icon: MapPin,
  question: '4. Which areas do you deliver to?',
  answer: "We mainly deliver around Heidelberg and surrounding areas. If you are outside this area, please contact us and we'll confirm availability."
}, {
  icon: CreditCard,
  question: '5. What payment methods do you accept?',
  answer: ['We accept:', '• EFT / Bank transfer', '• Cash', '• Other methods will be confirmed when you place your order']
}, {
  icon: Clock,
  question: '6. How long does it take to prepare an order?',
  answer: ['Standard orders: Usually same day or next day', 'Custom orders: Please allow 24–48 hours, depending on complexity']
}, {
  icon: Image,
  question: '7. Will the product look exactly like the pictures?',
  answer: 'We try our best to match the pictures, but because flowers and decorations may vary, small differences may occur while keeping the same style and quality.'
}, {
  icon: RefreshCw,
  question: '8. Do you offer refunds or returns?',
  answer: 'Because our products are custom-made and perishable, we generally do not offer refunds. However, if there is a problem with your order, please contact us immediately and we will assist.'
}, {
  icon: X,
  question: '9. Can I cancel my order?',
  answer: ['Orders can be cancelled within a limited time after payment.', 'Custom orders cannot be cancelled once preparation has started.']
}, {
  icon: ShoppingCart,
  question: '10. How do I place an order?',
  answer: ['You can place an order by:', '• WhatsApp', '• Phone call', '• Website', 'Just tell us what you want, your budget, and delivery details.']
}, {
  icon: Shield,
  question: '11. Is my personal information safe?',
  answer: 'Yes. We protect your personal information and handle it according to our POPIA Privacy Policy.'
}, {
  icon: Phone,
  question: '12. How do I contact you?',
  answer: ['You can contact us via:', '📱 WhatsApp / Phone: 0732736906', '📧 Email: sales@bloomandpop.co.za', '🌐 Website: www.bloomandpop.co.za']
}];
function FAQAccordion({
  item,
  index
}: {
  item: FAQItem;
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const Icon = item.icon;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.05
  }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2296">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full p-6 flex items-start gap-4 text-left hover:bg-gray-50 transition-colors" data-id="element-2297">
        <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-2298">
          <Icon className="w-5 h-5 text-rose-600" data-id="element-2299" />
        </div>
        <div className="flex-1" data-id="element-2300">
          <h3 className="font-bold text-gray-900 mb-1" data-id="element-2301">{item.question}</h3>
        </div>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} className="flex-shrink-0" data-id="element-2302">
          <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-2303" />
        </motion.div>
      </button>

      <AnimatePresence data-id="element-2304">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-2305">
            <div className="px-6 pb-6 ml-14" data-id="element-2306">
              {Array.isArray(item.answer) ? <div className="space-y-2 text-gray-700" data-id="element-2307">
                  {item.answer.map((line, i) => <p key={i} data-id="element-2308">{line}</p>)}
                </div> : <p className="text-gray-700" data-id="element-2309">{item.answer}</p>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function FAQPage() {
  const handleWhatsAppContact = () => {
    const message = `Hi! I have a question about Bloom & Pop.`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };
  return <div className="min-h-screen bg-white" data-id="element-2310">
      <Navbar data-id="element-2311" />

      <main className="pt-24 pb-16" data-id="element-2312">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-rose-50 to-white py-16" data-id="element-2313">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2314">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-2315">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-100 rounded-full mb-6" data-id="element-2316">
                <HelpCircle className="w-8 h-8 text-rose-600" data-id="element-2317" />
              </div>
              <h1 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-id="element-2318">
                Frequently Asked Questions
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-2319">
                Everything you need to know about Bloom & Pop Gift Shop
              </p>
            </motion.div>
          </div>
        </section>

        {/* FAQ Accordion */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-2320">
          <div className="space-y-4" data-id="element-2321">
            {faqs.map((faq, index) => <FAQAccordion key={index} item={faq} index={index} data-id="element-2322" />)}
          </div>
        </section>

        {/* Still Have Questions */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-2323">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className="bg-gradient-to-br from-rose-500 to-purple-600 rounded-2xl p-8 md:p-12 text-white text-center" data-id="element-2324">
            <HelpCircle className="w-12 h-12 mx-auto mb-4 opacity-90" data-id="element-2325" />
            <h2 className="font-serif text-3xl font-bold mb-4" data-id="element-2326">
              Still Have Questions?
            </h2>
            <p className="text-rose-50 mb-8 max-w-2xl mx-auto text-lg" data-id="element-2327">
              Can't find the answer you're looking for? Our friendly team is
              here to help you with anything you need!
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-2328">
              <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer" className="bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all p-4 rounded-xl border border-white/20" data-id="element-2329">
                <MessageCircle className="w-6 h-6 mx-auto mb-2" data-id="element-2330" />
                <p className="font-medium text-sm" data-id="element-2331">WhatsApp</p>
                <p className="text-xs text-rose-100 mt-1" data-id="element-2332">073 273 6906</p>
              </a>

              <a href="tel:0732736906" className="bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all p-4 rounded-xl border border-white/20" data-id="element-2333">
                <Phone className="w-6 h-6 mx-auto mb-2" data-id="element-2334" />
                <p className="font-medium text-sm" data-id="element-2335">Call Us</p>
                <p className="text-xs text-rose-100 mt-1" data-id="element-2336">073 273 6906</p>
              </a>

              <a href="mailto:sales@bloomandpop.co.za" className="bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all p-4 rounded-xl border border-white/20" data-id="element-2337">
                <Mail className="w-6 h-6 mx-auto mb-2" data-id="element-2338" />
                <p className="font-medium text-sm" data-id="element-2339">Email</p>
                <p className="text-xs text-rose-100 mt-1" data-id="element-2340">
                  sales@bloomandpop.co.za
                </p>
              </a>

              <a href="https://www.bloomandpop.co.za" target="_blank" rel="noopener noreferrer" className="bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all p-4 rounded-xl border border-white/20" data-id="element-2341">
                <Globe className="w-6 h-6 mx-auto mb-2" data-id="element-2342" />
                <p className="font-medium text-sm" data-id="element-2343">Website</p>
                <p className="text-xs text-rose-100 mt-1" data-id="element-2344">bloomandpop.co.za</p>
              </a>
            </div>

            <Button onClick={handleWhatsAppContact} className="bg-white text-rose-600 hover:bg-rose-50" size="lg" leftIcon={<MessageCircle className="w-5 h-5" data-id="element-2346" />} data-id="element-2345">
              Chat with Us on WhatsApp
            </Button>
          </motion.div>
        </section>

        {/* Quick Links */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-2347">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7
        }} data-id="element-2348">
            <h2 className="font-serif text-2xl font-bold text-gray-900 mb-6 text-center" data-id="element-2349">
              Helpful Resources
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-2350">
              <a href="/delivery-info" className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all text-center group" data-id="element-2351">
                <Truck className="w-8 h-8 text-rose-600 mx-auto mb-3 group-hover:scale-110 transition-transform" data-id="element-2352" />
                <h3 className="font-bold text-gray-900 mb-1" data-id="element-2353">Delivery Info</h3>
                <p className="text-sm text-gray-600" data-id="element-2354">
                  Learn about our delivery rates and areas
                </p>
              </a>

              <a href="/privacy-policy" className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all text-center group" data-id="element-2355">
                <Shield className="w-8 h-8 text-rose-600 mx-auto mb-3 group-hover:scale-110 transition-transform" data-id="element-2356" />
                <h3 className="font-bold text-gray-900 mb-1" data-id="element-2357">Privacy Policy</h3>
                <p className="text-sm text-gray-600" data-id="element-2358">
                  How we protect your information
                </p>
              </a>

              <a href="/contact" className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all text-center group" data-id="element-2359">
                <Phone className="w-8 h-8 text-rose-600 mx-auto mb-3 group-hover:scale-110 transition-transform" data-id="element-2360" />
                <h3 className="font-bold text-gray-900 mb-1" data-id="element-2361">Contact Us</h3>
                <p className="text-sm text-gray-600" data-id="element-2362">
                  Get in touch with our team
                </p>
              </a>
            </div>
          </motion.div>
        </section>
      </main>

      <Footer data-id="element-2363" />
    </div>;
}